import { useState } from 'react';
import { CheckCircle2, MapPin } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { subscribeApi } from '../services/api';
import {
  LOCATIONS,
  COMING_SOON_LOCATIONS,
  mapsLinkUrl,
  mapsEmbedUrl,
  type PurchaseLocation,
} from '../lib/locations';

function GetNotifiedForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setStatus('submitting');
    setMessage('');
    try {
      const res = await subscribeApi.subscribe(email);
      setStatus('done');
      setMessage(res.message);
      setEmail('');
    } catch (err: unknown) {
      setStatus('error');
      setMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  }

  if (status === 'done') {
    return (
      <div className="flex flex-col items-center gap-3" role="status">
        <CheckCircle2 className="w-10 h-10" />
        <p className="text-lg">{message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col items-center gap-2">
      <div className="flex gap-2 w-full">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          className="flex-1 px-4 py-3 rounded-lg text-gray-900"
          disabled={status === 'submitting'}
        />
        <Button type="submit" variant="secondary" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Subscribing…' : 'Subscribe'}
        </Button>
      </div>
      {status === 'error' && (
        <p className="text-sm text-red-100 bg-red-700/40 rounded px-3 py-1" role="alert">{message}</p>
      )}
    </form>
  );
}

function LocationCard({ location, comingSoon = false }: { location: PurchaseLocation; comingSoon?: boolean }) {
  return (
    <div className="bg-white border-2 border-coastal-200 rounded-lg overflow-hidden shadow-sm">
      <div className="flex items-start gap-3 p-6">
        <a
          href={mapsLinkUrl(location)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${location.name} in Google Maps`}
          title="Open in Google Maps"
          className="w-11 h-11 rounded-full bg-coastal-50 flex items-center justify-center flex-shrink-0 hover:bg-coastal-100 transition-colors"
        >
          <MapPin className="w-6 h-6 text-coastal-600" />
        </a>
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-xl font-bold text-coastal-900">{location.name}</h3>
            {comingSoon && (
              <span className="text-xs font-semibold uppercase tracking-wide bg-gold-100 text-gold-800 border border-gold-300 rounded-full px-2.5 py-0.5 whitespace-nowrap">
                Coming Soon
              </span>
            )}
          </div>
          <p className="text-gray-600 text-sm">{location.addressLine}</p>
          <p className="text-gray-600 text-sm">{location.cityStateZip}</p>
        </div>
      </div>
      <iframe
        src={mapsEmbedUrl(location)}
        title={`Map of ${location.name}`}
        className="w-full h-48 border-0 block"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
    </div>
  );
}

export function LocationsPage() {
  return (
    <div>
      <section className="bg-cream py-12">
        <div className="container-max">
          <h1 className="text-4xl font-serif font-bold text-coastal-600 mb-2">Where to Find Us</h1>
          <p className="text-gray-700">Here&apos;s where you can find our products</p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container-max">
          <h2 className="text-2xl font-serif font-bold text-coastal-700 mb-6">Available Now</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {LOCATIONS.map((location) => (
              <LocationCard key={location.name} location={location} />
            ))}
          </div>

          <h2 className="text-2xl font-serif font-bold text-coastal-700 mb-6">Coming Soon</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {COMING_SOON_LOCATIONS.map((location) => (
              <LocationCard key={location.name} location={location} comingSoon />
            ))}
          </div>

          <div className="bg-coastal-50 border-2 border-coastal-200 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-coastal-900 mb-3">More Locations on the Way</h2>
            <p className="text-gray-700">Stay tuned.</p>
          </div>
        </div>
      </section>

      <section className="bg-coastal-600 text-white py-16">
        <div className="container-max max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Get Notified</h2>
          <p className="mb-8 opacity-90">
            Be the first to know when we add new locations.
          </p>
          <GetNotifiedForm />
        </div>
      </section>
    </div>
  );
}
